import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Send,
  Flag,
  RefreshCw,
  Lightbulb,
  AlertTriangle,
  Sparkles,
  Download,
  Target,
  Shield,
  MessageCircle,
  CheckCircle2,
  Volume2,
  VolumeX,
  Mic,
  MicOff,
  PhoneOff,
  Phone,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScoreRing } from "@/components/score-ring";
import { LogoMark } from "@/components/brand/logo";
import { Confetti } from "@/components/confetti";
import {
  getPracticeSession,
  sendPracticeMessage,
  getSessionFeedback,
  getPracticeSpeechSource,
  startPracticeSession,
  getScenarios,
  getCoachProfile,
  updateCoachProfile,
  generateCoachPlan,
  createVoiceSession,
  endVoiceCall,
  saveVoiceTranscript,
  checkVoiceSafety,
} from "@/data/requests/practice-requests";
import { applyStatusToPersonal, PENDING_STATUS_KEY } from "@/lib/personal-plan";
import { BookOpen } from "lucide-react";
import AuthStore from "@/stores/AuthStore";

// Guided "what to practice next" — `good` advances when it went well, `tough` recovers.
const NEXT_MAP = {
  "texting-someone-you-like": { good: "first-date", tough: "handling-rejection" },
  "first-date": { good: "define-the-relationship", tough: "decline-second-date" },
  "decline-second-date": { good: "handling-rejection", tough: "setting-boundaries" },
  "define-the-relationship": { good: "mental-load-talk", tough: "conflict-repair" },
  "conflict-repair": { good: "mental-load-talk", tough: "responding-to-gaslighting" },
  "mental-load-talk": { good: "define-the-relationship", tough: "conflict-repair" },
  "responding-to-gaslighting": { good: "setting-boundaries", tough: "setting-boundaries" },
  "setting-boundaries": { good: "persistent-match", tough: "love-bombing" },
  "persistent-match": { good: "love-bombing", tough: "setting-boundaries" },
  "love-bombing": { good: "setting-boundaries", tough: "setting-boundaries" },
  "breakup-conversation": { good: "handling-rejection", tough: "conflict-repair" },
  "returning-ex": { good: "setting-boundaries", tough: "breakup-conversation" },
  "handling-rejection": { good: "texting-someone-you-like", tough: "texting-someone-you-like" },
};

function getStarterReplies(session, t) {
  const scenario = session?.scenario;
  const text = `${scenario?.title || ""} ${scenario?.description || ""} ${scenario?.goalHint || ""}`.toLowerCase();

  if (text.includes("boundary") || text.includes("no")) {
    return [
      t("practiceSession.starters.boundary.0"),
      t("practiceSession.starters.boundary.1"),
      t("practiceSession.starters.boundary.2"),
    ];
  }

  if (text.includes("breakup") || text.includes("ending")) {
    return [
      t("practiceSession.starters.breakup.0"),
      t("practiceSession.starters.breakup.1"),
      t("practiceSession.starters.breakup.2"),
    ];
  }

  if (text.includes("text")) {
    return [
      t("practiceSession.starters.texting.0"),
      t("practiceSession.starters.texting.1"),
      t("practiceSession.starters.texting.2"),
    ];
  }

  if (text.includes("fight") || text.includes("repair") || text.includes("conflict")) {
    return [
      t("practiceSession.starters.conflict.0"),
      t("practiceSession.starters.conflict.1"),
      t("practiceSession.starters.conflict.2"),
    ];
  }

  return [
    t("practiceSession.starters.default.0"),
    t("practiceSession.starters.default.1"),
    t("practiceSession.starters.default.2"),
  ];
}

function getScenarioCopy(scenario, t) {
  if (!scenario) {
    return {
      title: t("practiceSession.defaultTitle"),
      description: "",
      goalHint: "",
    };
  }

  return {
    title: t(`practice.scenarios.${scenario.slug}.title`, {
      defaultValue: scenario.title,
    }),
    description: t(`practice.scenarios.${scenario.slug}.description`, {
      defaultValue: scenario.description,
    }),
    goalHint: t(`practice.scenarios.${scenario.slug}.goalHint`, {
      defaultValue: scenario.goalHint || "",
    }),
  };
}

function CoachPanel({ session, userTurns, isActive, feedbackPending, onFinish, canFinish }) {
  const { t } = useTranslation();
  const difficulty = session?.scenario?.difficulty || 1;
  const pushback = difficulty === 3 ? t("practiceSession.pushback.strong") : difficulty === 2 ? t("practiceSession.pushback.medium") : t("practiceSession.pushback.light");
  const scenarioCopy = getScenarioCopy(session?.scenario, t);

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          {t("practiceSession.cockpit.title")}
        </CardTitle>
        <CardDescription>
          {t("practiceSession.cockpit.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="rounded-lg border bg-muted/40 p-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">{t("practiceSession.cockpit.goal")}</p>
          <p>{scenarioCopy.goalHint || t("practiceSession.cockpit.defaultGoal")}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground mb-1">{t("practiceSession.cockpit.pushback")}</p>
            <p className="font-medium">{pushback}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground mb-1">{t("practiceSession.cockpit.yourTurns")}</p>
            <p className="font-medium">{userTurns}</p>
          </div>
        </div>
        <div className="space-y-2">
          {[
            t("practiceSession.cockpit.tip1"),
            t("practiceSession.cockpit.tip2"),
            t("practiceSession.cockpit.tip3"),
          ].map((item) => (
            <div key={item} className="flex gap-2 text-muted-foreground">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        {isActive && (
          <Button
            className="w-full"
            onClick={onFinish}
            disabled={feedbackPending || !canFinish}
          >
            <Flag className="mr-2 h-4 w-4" />
            {feedbackPending
              ? t("practiceSession.actions.reviewingShort")
              : canFinish
              ? t("practiceSession.actions.endShort")
              : t("practiceSession.actions.sendTwoFirst")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackPanel({ feedback, scenarioTitle, onTryAgain, tryAgainPending, recommendation, onStartNext, startNextPending }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const overall = Math.round(
    (feedback.clarityScore + feedback.empathyScore + feedback.boundaryScore) / 3
  );
  const headline =
    feedback.shareHeadline ||
    (feedback.boundaryScore >= feedback.empathyScore &&
    feedback.boundaryScore >= feedback.clarityScore
      ? t("practiceSession.feedback.headlineBoundary")
      : feedback.empathyScore >= feedback.clarityScore
      ? t("practiceSession.feedback.headlineEmpathy")
      : t("practiceSession.feedback.headlineClarity"));

  const saveImage = async () => {
    setSaving(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = "pairvio-score.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast({ title: t("practiceSession.toast.saved"), description: t("practiceSession.toast.scoreDownloaded") });
    } catch (e) {
      toast({ title: t("practiceSession.toast.couldNotSave"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {overall >= 8 && <Confetti />}
      {/* Shareable score card — elevated, story-format */}
      <div
        ref={cardRef}
        style={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 26,
          background: "linear-gradient(160deg, #0F172A 0%, #1E293B 55%, #334155 100%)",
          color: "#F4F5FF",
          padding: "26px 24px 22px",
        }}
      >
        <div style={{ position: "absolute", top: -60, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(201,148,218,0.30)" }} />
        <div style={{ position: "absolute", bottom: -80, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(201,148,218,0.16)" }} />

        <div style={{ position: "relative" }}>
          <div className="flex items-center gap-2 mb-7">
            <LogoMark size={22} />
            <span className="font-semibold" style={{ fontFamily: '"Fraunces", serif' }}>Pairvio</span>
          </div>

          <p style={{ fontSize: 11, letterSpacing: "0.16em", opacity: 0.6, fontWeight: 500 }}>
            {t("practiceSession.feedback.iPracticed").toUpperCase()}
          </p>
          <p style={{ fontFamily: '"Fraunces", serif', fontSize: 21, fontWeight: 600, marginTop: 4, color: "#FECACA" }}>
            {scenarioTitle}
          </p>

          <p style={{ fontFamily: '"Fraunces", serif', fontStyle: "italic", fontSize: 30, lineHeight: 1.2, fontWeight: 500, marginTop: 20 }}>
            {headline}
          </p>

          <div className="flex items-end gap-2" style={{ marginTop: 20 }}>
            <span style={{ fontFamily: '"Fraunces", serif', fontSize: 64, lineHeight: 0.85, fontWeight: 600 }}>{overall}</span>
            <span style={{ fontSize: 14, opacity: 0.7, paddingBottom: 8 }}>/10 {t("practiceSession.feedback.overall")}</span>
          </div>

          <div className="grid grid-cols-3 gap-2" style={{ marginTop: 20 }}>
            {[
              [t("practiceSession.feedback.clarity"), feedback.clarityScore, false],
              [t("practiceSession.feedback.empathy"), feedback.empathyScore, false],
              [t("practiceSession.feedback.boundaries"), feedback.boundaryScore, true],
            ].map(([l, v, hi]) => (
              <div key={l} style={{ borderRadius: 14, padding: "10px 6px", textAlign: "center", background: hi ? "rgba(201,148,218,0.24)" : "rgba(251,248,241,0.1)" }}>
                <div style={{ fontFamily: '"Fraunces", serif', fontSize: 19, fontWeight: 600, color: hi ? "#FECACA" : "#F4F5FF" }}>{v}</div>
                <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between" style={{ marginTop: 22, fontSize: 11.5, opacity: 0.55 }}>
            <span>pairvio.app</span>
            <span>{t("practiceSession.feedback.tagline")}</span>
          </div>
        </div>
      </div>

      <Card className="rounded-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t("practiceSession.feedback.nextRunPlan")}</CardTitle>
          <CardDescription>
            {t("practiceSession.feedback.nextRunDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-around py-2">
            <ScoreRing value={feedback.clarityScore} label={t("practiceSession.feedback.clarity")} />
            <ScoreRing value={feedback.empathyScore} label={t("practiceSession.feedback.empathy")} />
            <ScoreRing value={feedback.boundaryScore} label={t("practiceSession.feedback.boundaries")} />
          </div>
          <div>
            <h4 className="font-medium mb-1">{t("practiceSession.feedback.landedWell")}</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{feedback.strengths}</p>
          </div>
          <div>
            <h4 className="font-medium mb-1">{t("practiceSession.feedback.sharpen")}</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{feedback.improvements}</p>
          </div>
          {feedback.suggestedNext && (
            <div className="rounded-xl border bg-muted/50 p-3">
              <h4 className="font-medium mb-1 flex items-center gap-2 text-sm">
                <Lightbulb className="h-4 w-4" /> {t("practiceSession.feedback.trySaying")}
              </h4>
              <p className="text-sm italic">"{feedback.suggestedNext}"</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={saveImage} disabled={saving}>
              <Download className="mr-2 h-4 w-4" /> {saving ? t("practiceSession.actions.saving") : t("practiceSession.actions.saveCard")}
            </Button>
            <Button onClick={onTryAgain} disabled={tryAgainPending}>
              <RefreshCw className="mr-2 h-4 w-4" /> {t("practiceSession.actions.tryAgain")}
            </Button>
          </div>

          {recommendation && (
            <button
              onClick={() => onStartNext(recommendation.next.slug)}
              disabled={startNextPending}
              className="w-full text-left rounded-xl border bg-card p-4 transition hover:shadow-sm"
              style={{ borderColor: "#5865F2" }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="h-3.5 w-3.5" style={{ color: "#5865F2" }} />
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#5865F2" }}>
                  {recommendation.path === "good" ? t("practiceSession.next.goodEyebrow") : t("practiceSession.next.toughEyebrow")}
                </span>
              </div>
              <div className="font-semibold mb-2">
                {t(`practice.scenarios.${recommendation.next.slug}.title`, { defaultValue: recommendation.next.title })}
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "#5865F2" }}>
                {t("practiceSession.next.cta")} <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PracticeSessionScreen() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [voiceRepliesEnabled, setVoiceRepliesEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const bottomRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const dataChannelRef = useRef(null);
  const voiceCallIdRef = useRef(null);
  const callTimerRef = useRef(null);
  const [callMode, setCallMode] = useState(false);
  const [callPhase, setCallPhase] = useState("idle");
  const [callMuted, setCallMuted] = useState(false);
  const [callElapsed, setCallElapsed] = useState(0);
  const [callError, setCallError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["practice-session", sessionId],
    queryFn: () => getPracticeSession(sessionId),
  });

  const session = data?.data;
  const messages = session?.messages || [];
  const feedback = session?.feedback;
  const isActive = session?.status === "ACTIVE";
  const scenarioCopy = getScenarioCopy(session?.scenario, t);

  const { data: scenariosData } = useQuery({ queryKey: ["scenarios"], queryFn: getScenarios });
  const recommendation = (() => {
    if (!feedback || !session?.scenario) return null;
    const scenarios = scenariosData?.data || [];
    if (!scenarios.length) return null;
    const overall = Math.round((feedback.clarityScore + feedback.empathyScore + feedback.boundaryScore) / 3);
    const pathKey = overall >= 6 ? "good" : "tough";
    const map = NEXT_MAP[session.scenario.slug];
    let next = map ? scenarios.find((s) => s.slug === map[pathKey]) : null;
    if (!next) {
      const sameCat = scenarios.filter((s) => s.category === session.scenario.category && s.slug !== session.scenario.slug);
      next = (sameCat.length ? sameCat : scenarios.filter((s) => s.slug !== session.scenario.slug))[0] || null;
    }
    return next ? { next, path: pathKey } : null;
  })();
  const startNextMutation = useMutation({
    mutationFn: (scenarioId) => startPracticeSession(scenarioId, i18n.language),
    onSuccess: (res) => navigate(`/practice/${res.data.id}`),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["practice-session", sessionId] });

  const stopVoice = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setIsSpeaking(false);
  };

  const finishVoiceUsage = () => {
    const id = voiceCallIdRef.current;
    if (!id) return;
    voiceCallIdRef.current = null;
    endVoiceCall(id).catch(() => {});
  };

  const hangUpCall = () => {
    if (callTimerRef.current) clearTimeout(callTimerRef.current);
    callTimerRef.current = null;
    dataChannelRef.current?.close();
    peerRef.current?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    dataChannelRef.current = null;
    peerRef.current = null;
    streamRef.current = null;
    finishVoiceUsage();
    setCallMode(false);
    setCallPhase("idle");
    setCallMuted(false);
    setCallElapsed(0);
    refresh();
  };

  const startVoiceCall = async () => {
    if (callMode) return;
    stopVoice();
    setCallMode(true);
    setCallPhase("connecting");
    setCallError("");
    setCallElapsed(0);
    try {
      const preferredVoice = localStorage.getItem("pairvioRealtimeVoice") || "marin";
      const { data: voice } = await createVoiceSession(sessionId, i18n.language, preferredVoice);
      voiceCallIdRef.current = voice.voiceCallId;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const peer = new RTCPeerConnection();
      peerRef.current = peer;
      stream.getTracks().forEach((track) => peer.addTrack(track, stream));
      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      peer.ontrack = (event) => { remoteAudio.srcObject = event.streams[0]; };
      const channel = peer.createDataChannel("oai-events");
      dataChannelRef.current = channel;
      channel.onopen = () => setCallPhase("live");
      channel.onclose = () => setCallPhase((phase) => phase === "idle" ? phase : "disconnected");
      channel.onmessage = async ({ data }) => {
        let event;
        try { event = JSON.parse(data); } catch { return; }
        if (event.type === "input_audio_buffer.speech_started") setCallPhase("listening");
        if (event.type === "input_audio_buffer.speech_stopped") setCallPhase("thinking");
        if (event.type === "conversation.item.input_audio_transcription.completed") {
          const text = (event.transcript || "").trim();
          if (!text) return;
          saveVoiceTranscript(sessionId, "user", text).catch(() => {});
          try {
            const safety = await checkVoiceSafety(sessionId, text);
            if (!safety?.data?.safety?.blocked && channel.readyState === "open") {
              channel.send(JSON.stringify({ type: "response.create" }));
            }
          } catch {
            setCallError(t("practiceSession.call.connectionLost"));
          }
        }
        if (event.type === "response.output_audio.delta" || event.type === "output_audio_buffer.started") setCallPhase("speaking");
        if (event.type === "output_audio_buffer.stopped" || event.type === "output_audio_buffer.cleared") setCallPhase("live");
        if (event.type === "response.output_audio_transcript.done" || event.type === "response.audio_transcript.done") {
          const text = (event.transcript || "").trim();
          if (text) saveVoiceTranscript(sessionId, "counterpart", text).catch(() => {});
          setCallPhase("live");
        }
        if (event.type === "error") setCallError(t("practiceSession.call.liveFailedBody"));
      };
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const answer = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(voice.model)}`, {
        method: "POST",
        body: offer.sdp,
        headers: { Authorization: `Bearer ${voice.clientSecret}`, "Content-Type": "application/sdp" },
      });
      if (!answer.ok) throw new Error("Realtime connection failed");
      await peer.setRemoteDescription({ type: "answer", sdp: await answer.text() });
      callTimerRef.current = setTimeout(hangUpCall, Math.max(1, voice.maxCallSeconds || 600) * 1000);
    } catch (error) {
      const code = error?.response?.data?.code;
      if (error?.response?.status === 402 && code) {
        const message = t(`practiceSession.call.limits.${code}`);
        hangUpCall();
        toast({ title: t("practiceSession.call.trialLimitTitle"), description: message });
        return;
      }
      setCallError(error?.name === "NotAllowedError" ? t("practiceSession.call.micPermission") : t("practiceSession.call.liveFailedBody"));
      setCallPhase("error");
      finishVoiceUsage();
    }
  };

  const toggleCallMute = () => {
    const next = !callMuted;
    streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !next; });
    setCallMuted(next);
  };

  useEffect(() => {
    if (!callMode || callPhase === "connecting") return;
    const id = setInterval(() => setCallElapsed((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [callMode, callPhase]);

  useEffect(() => () => {
    stopVoice();
    dataChannelRef.current?.close();
    peerRef.current?.close();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    finishVoiceUsage();
  }, []);

  const speakCoachReply = async (text) => {
    if (!voiceRepliesEnabled) return;
    const clean = (text || "").trim();
    if (!clean) return;

    try {
      stopVoice();
      const response = await fetch(getPracticeSpeechSource(clean.slice(0, 1000), i18n.language), {
        headers: { Authorization: `Bearer ${AuthStore.getToken?.() || localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Speech request failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = stopVoice;
      audio.onerror = stopVoice;
      setIsSpeaking(true);
      await audio.play();
    } catch (error) {
      stopVoice();
      toast({
        title: t("voice.failedTitle"),
        description: t("voice.failedDescription"),
        variant: "destructive",
      });
    }
  };

  const sendMutation = useMutation({
    mutationFn: (content) => sendPracticeMessage(sessionId, content, i18n.language),
    onSuccess: (res) => {
      speakCoachReply(res?.data?.message?.content);
      refresh();
    },
    onError: (err) =>
      toast({
        title: t("practiceSession.errors.message"),
        description: err?.response?.data?.error || t("common.tryAgain"),
        variant: "destructive",
      }),
  });

  // coachNote + suggestedStatus ride only on the fresh feedback response —
  // capture them here (they're not stored with the feedback row).
  const [extras, setExtras] = useState({});
  const feedbackMutation = useMutation({
    mutationFn: () => getSessionFeedback(sessionId, i18n.language),
    onSuccess: (res) => {
      const { coachNote, suggestedStatus } = res?.data || {};
      setExtras({ coachNote, suggestedStatus });
      if (suggestedStatus) {
        try {
          localStorage.setItem(PENDING_STATUS_KEY, JSON.stringify({ status: suggestedStatus, ts: Date.now() }));
        } catch {}
      }
      refresh();
      queryClient.invalidateQueries({ queryKey: ["practice-sessions"] });
    },
    onError: (err) =>
      toast({
        title: t("practiceSession.errors.feedback"),
        description: err?.response?.data?.error || t("common.tryAgain"),
        variant: "destructive",
      }),
  });

  const tryAgainMutation = useMutation({
    mutationFn: () => startPracticeSession(session.scenarioId),
    onSuccess: (res) => navigate(`/practice/${res.data.id}`),
  });

  // Confirm-first life-status change (suggested by the feedback).
  const [statusPrompt, setStatusPrompt] = useState(null); // "saving" | "saved" | "dismissed" | null
  const [newGoal, setNewGoal] = useState(null);
  const applyStatusChange = async () => {
    if (!extras.suggestedStatus || statusPrompt === "saving") return;
    try {
      setStatusPrompt("saving");
      const profile = (await getCoachProfile()).data;
      const updated = applyStatusToPersonal(profile?.personal, extras.suggestedStatus);
      await updateCoachProfile({ personal: updated, situationNotes: "" });
      try { await generateCoachPlan(i18n.language); } catch {}
      queryClient.invalidateQueries({ queryKey: ["coach-profile"] });
      setNewGoal(updated.goal || null);
      setStatusPrompt("saved");
      try { localStorage.removeItem(PENDING_STATUS_KEY); } catch {}
    } catch {
      setStatusPrompt(null);
    }
  };
  const dismissStatusChange = () => {
    setStatusPrompt("dismissed");
    try { localStorage.removeItem(PENDING_STATUS_KEY); } catch {}
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, sendMutation.isPending]);

  const handleSend = (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sendMutation.isPending) return;
    setInput("");
    sendMutation.mutate(content);
  };

  const userTurns = messages.filter((m) => m.role === "USER").length;
  const starterReplies = getStarterReplies(session, t);
  const canFinish = userTurns >= 2;

  return (
    <AppLayout
      title={scenarioCopy.title}
      description={scenarioCopy.goalHint}
      loading={isLoading}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4">
          <Card className="flex flex-col rounded-lg overflow-hidden" style={{ minHeight: "64vh" }}>
            <div className="flex items-center gap-2 border-b px-4 py-2 text-xs text-muted-foreground bg-secondary/45">
              <Sparkles className="h-3.5 w-3.5" />
              {t("practiceSession.aiNotice")}
              {session?.personalized && (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                  style={{ backgroundColor: "rgba(30,41,59,0.11)", color: "#1E293B" }}
                >
                  {t("practiceSession.personalizedBadge")}
                </span>
              )}
              <Button
                type="button"
                size="sm"
                variant={voiceRepliesEnabled ? "default" : "outline"}
                className="ml-auto h-8"
                onClick={() => {
                  const next = !voiceRepliesEnabled;
                  setVoiceRepliesEnabled(next);
                  if (!next) stopVoice();
                }}
              >
                {voiceRepliesEnabled ? <Volume2 className="mr-1.5 h-3.5 w-3.5" /> : <VolumeX className="mr-1.5 h-3.5 w-3.5" />}
                {voiceRepliesEnabled ? t("voice.repliesOn") : t("voice.repliesOff")}
              </Button>
              {isActive && (
                <Button type="button" size="sm" className="h-8" onClick={startVoiceCall}>
                  <Phone className="mr-1.5 h-3.5 w-3.5" />
                  {t("practiceSession.call.start")}
                </Button>
              )}
            </div>
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="py-8 text-center max-w-xl mx-auto">
                  <div className="w-12 h-12 mx-auto rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <p className="font-medium mb-1">{t("practiceSession.empty.title")}</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("practiceSession.empty.description")}
                  </p>
                  {isActive && (
                    <div className="flex flex-wrap justify-center gap-2">
                      {starterReplies.map((starter) => (
                        <button
                          key={starter}
                          type="button"
                          onClick={() => setInput(starter)}
                          className="rounded-full border bg-card px-3 py-1.5 text-xs text-left hover:border-primary/50 hover:bg-accent transition-colors"
                        >
                          {starter}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {messages.map((m) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className={`flex items-end gap-2 ${
                    m.role === "USER" ? "justify-end" : "justify-start"
                  }`}
                >
                  {m.role !== "USER" && (
                    <div
                      className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs ${
                        m.role === "COACH"
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200"
                          : "bg-primary/15 text-primary"
                      }`}
                    >
                      {m.role === "COACH" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] px-4 py-2.5 text-sm whitespace-pre-line shadow-sm ${
                      m.role === "USER"
                        ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md"
                        : m.role === "COACH"
                        ? "border border-amber-300 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100 rounded-2xl rounded-bl-md"
                        : "bg-muted text-foreground rounded-2xl rounded-bl-md"
                    }`}
                  >
                    {m.role === "COACH" && (
                      <p className="flex items-center gap-1 text-xs font-semibold mb-1">
                        {t("practiceSession.coach")}
                      </p>
                    )}
                    {m.content}
                  </div>
                </motion.div>
              ))}
              {sendMutation.isPending && (
                <div className="flex items-end gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center bg-primary/15 text-primary">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                  <div className="rounded-2xl rounded-bl-md bg-muted px-4 py-3 flex items-center gap-1">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60"
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </CardContent>

            {isActive && (
              <form onSubmit={handleSend} className="border-t p-3 bg-card">
                {messages.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {starterReplies.map((starter) => (
                      <button
                        key={starter}
                        type="button"
                        onClick={() => setInput(starter)}
                        className="shrink-0 rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground hover:border-primary/50"
                      >
                        {starter}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) handleSend(e);
                  }}
                  placeholder={t("practiceSession.inputPlaceholder")}
                  rows={1}
                  className="resize-none"
                  maxLength={4000}
                />
                <Button type="submit" disabled={sendMutation.isPending || !input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
                </div>
              </form>
            )}
          </Card>

          {isActive && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => feedbackMutation.mutate()}
              disabled={feedbackMutation.isPending || !canFinish}
            >
              <Flag className="mr-2 h-4 w-4" />
              {feedbackMutation.isPending
                ? t("practiceSession.actions.reviewing")
                : !canFinish
                ? t("practiceSession.actions.sendFew")
                : t("practiceSession.actions.end")}
            </Button>
          )}
          {isSpeaking && (
            <div className="rounded-lg border bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100 flex items-center justify-center gap-2">
              <Volume2 className="h-4 w-4" />
              {t("voice.aiSpeaking")}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {feedback && extras.coachNote && (
            <Card className="p-4 flex items-start gap-3" style={{ backgroundColor: "rgba(30,41,59,0.08)" }}>
              <BookOpen className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#1E293B" }} />
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-wide" style={{ color: "#1E293B" }}>
                  {t("practiceSession.feedback.coachNoted")}
                </p>
                <p className="text-sm font-medium mt-0.5">{extras.coachNote}</p>
              </div>
            </Card>
          )}
          {feedback && extras.suggestedStatus && statusPrompt !== "dismissed" && (
            <Card className="p-4">
              {statusPrompt === "saved" ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {newGoal
                      ? t("practiceSession.statusChange.savedWithGoal", {
                          goal: t(`personalSetup.goals.${newGoal}`, { defaultValue: newGoal }),
                        })
                      : t("practiceSession.statusChange.saved")}
                  </p>
                  <button
                    className="text-sm font-semibold mt-2"
                    style={{ color: "#1E293B" }}
                    onClick={() => navigate("/personal-setup")}
                  >
                    {t("practiceSession.statusChange.changeGoal")}
                  </button>
                </>
              ) : (
                <>
                  <p className="font-semibold text-sm mb-1">
                    {t(`practiceSession.statusChange.lead.${extras.suggestedStatus}`, {
                      defaultValue: t("practiceSession.statusChange.lead.generic"),
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t("practiceSession.statusChange.question", {
                      status: t(`personalSetup.status.${extras.suggestedStatus}`, {
                        defaultValue: extras.suggestedStatus,
                      }),
                    })}
                  </p>
                  <div className="flex items-center gap-4">
                    <Button size="sm" disabled={statusPrompt === "saving"} onClick={applyStatusChange}>
                      {t("practiceSession.statusChange.yes")}
                    </Button>
                    <button className="text-sm font-medium text-muted-foreground" onClick={dismissStatusChange}>
                      {t("practiceSession.statusChange.no")}
                    </button>
                  </div>
                </>
              )}
            </Card>
          )}
          {feedback ? (
            <FeedbackPanel
              feedback={feedback}
              scenarioTitle={scenarioCopy.title}
              onTryAgain={() => tryAgainMutation.mutate()}
              tryAgainPending={tryAgainMutation.isPending}
              recommendation={recommendation}
              onStartNext={(slug) => {
                const sc = (scenariosData?.data || []).find((s) => s.slug === slug);
                if (sc) startNextMutation.mutate(sc.id);
              }}
              startNextPending={startNextMutation.isPending}
            />
          ) : (
            <>
              <CoachPanel
                session={session}
                userTurns={userTurns}
                isActive={isActive}
                feedbackPending={feedbackMutation.isPending}
                onFinish={() => feedbackMutation.mutate()}
                canFinish={canFinish}
              />
              <Card className="rounded-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    {t("practiceSession.guardrails.title")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p>
                    {t("practiceSession.guardrails.ai")}
                  </p>
                  {session?.scenario?.description && (
                    <div className="rounded-lg border p-3 mt-2 bg-muted/40">
                      <p className="font-medium text-foreground mb-1">{t("practiceSession.guardrails.scenario")}</p>
                      <p>{scenarioCopy.description}</p>
                    </div>
                  )}
                  <p className="text-xs pt-2">
                    {t("practiceSession.guardrails.disclaimer")}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
          <Badge variant="secondary" className="w-full justify-center py-1">
            {session?.status === "COMPLETED"
              ? t("practice.status.completed")
              : session?.status === "ABANDONED"
              ? t("practice.status.abandoned")
              : t("practice.status.active")}
          </Badge>
        </div>
      </div>
      {callMode && (
        <div className="fixed inset-0 z-[100] flex min-h-[100dvh] flex-col items-center justify-between bg-[#0F172A] px-5 pb-8 pt-[max(2rem,env(safe-area-inset-top))] text-white">
          <div className="text-center">
            <p className="text-sm font-semibold text-white/55">{scenarioCopy.title}</p>
            <p className="mt-2 text-xs text-white/35">{t("practiceSession.call.disclaimer")}</p>
          </div>
          <div className="flex flex-col items-center">
            <div className={`flex h-48 w-48 items-center justify-center rounded-full transition-all ${callPhase === "speaking" ? "bg-[#1E293B]/70 scale-110" : "bg-[#1E293B]/25"}`}>
              <div className={`h-28 w-28 rounded-full ${callPhase === "listening" ? "animate-pulse bg-[#5865F2]" : "bg-[#1E293B]"}`} />
            </div>
            <p className="mt-8 text-xl font-medium text-white/80">
              {callError || t(`practiceSession.call.${callPhase === "error" || callPhase === "disconnected" ? "connectionLost" : callPhase}`)}
            </p>
          </div>
          <div className="w-full max-w-xl space-y-5">
            <div className="flex items-center justify-around rounded-[2rem] bg-white/10 px-5 py-4 backdrop-blur">
              <button onClick={hangUpCall} className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FF5F65]" aria-label="End call"><PhoneOff /></button>
              <span className="min-w-16 text-center text-lg font-semibold tabular-nums">{Math.floor(callElapsed / 60)}:{String(callElapsed % 60).padStart(2, "0")}</span>
              <button onClick={toggleCallMute} className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10" aria-label="Mute">{callMuted ? <MicOff /> : <Mic />}</button>
            </div>
            <button onClick={hangUpCall} className="w-full rounded-2xl bg-white/10 py-4 text-base font-semibold text-white/75">{t("practiceSession.call.sendText")}</button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
